import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Upload, Trash2, Image } from "lucide-react";

interface LegacyActivitiesSectionProps {
  value: any[];
  onChange: (v: any[]) => void;
  mode: "edit" | "view";
}

export function LegacyActivitiesSection({ value, onChange, mode }: LegacyActivitiesSectionProps) {
  const isReadOnly = mode === "view";
  
  const addActivityRow = () => {
    const newActivity = {
      activity: "",
      location: "",
      completion: 0,
      responsible: "",
      description: "",
      photos: []
    };
    onChange([...value, newActivity]);
  };

  const updateActivityRow = (index: number, field: string, fieldValue: any) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: fieldValue };
    onChange(updated);
  };

  const removeActivityRow = (index: number) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <Card data-section="activities">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Atividades
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!isReadOnly ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs w-1/4">Atividade</TableHead>
                  <TableHead className="text-xs w-1/5">Local</TableHead>
                  <TableHead className="text-xs text-center w-20">% Concluída</TableHead>
                  <TableHead className="text-xs w-1/6">Responsável</TableHead>
                  <TableHead className="text-xs w-1/3">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {value.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        value={item.activity}
                        onChange={(e) => updateActivityRow(index, 'activity', e.target.value)}
                        placeholder="Ex: Concretagem da laje do 3º andar"
                        className="text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.location || ''}
                        onChange={(e) => updateActivityRow(index, 'location', e.target.value)}
                        placeholder="Ex: Bloco A - 3º andar"
                        className="text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={item.completion || 0}
                        onChange={(e) => updateActivityRow(index, 'completion', parseInt(e.target.value) || 0)}
                        min="0"
                        max="100"
                        className="text-xs text-center"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.responsible || ''}
                        onChange={(e) => updateActivityRow(index, 'responsible', e.target.value)}
                        placeholder="Ex: João Silva"
                        className="text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeActivityRow(index)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {(!value || value.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Nenhuma atividade adicionada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            
            <Button
              variant="outline"
              onClick={addActivityRow}
              className="w-full mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Atividade
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Atividade</TableHead>
                  <TableHead className="text-xs">Local</TableHead>
                  <TableHead className="text-xs text-center">% Concluída</TableHead>
                  <TableHead className="text-xs">Responsável</TableHead>
                  <TableHead className="text-xs">Descrição</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {value?.map((activity, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-xs">
                      {activity.activity}
                      {activity.linkedTaskTitle && (
                        <span className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                          <FileText className="w-3 h-3" />
                          {activity.linkedTaskTitle}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{activity.location}</TableCell>
                    <TableCell className="text-xs text-center">
                      <Badge variant={activity.completion === 100 ? "default" : "secondary"}>
                        {activity.completion}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{activity.responsible}</TableCell>
                    <TableCell className="text-xs">{activity.description}</TableCell>
                  </TableRow>
                )) || []}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}